import { Anchor, Badge, Button, Group, Spoiler, Stack, Text, Typography } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconChevronDown, IconChevronUp, IconCopy, IconEdit, IconMailFast, IconTrash } from "@tabler/icons-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { DetailCard } from "shared/components/global/DetailCard";
import { PropertyBadge } from "shared/components/global/PropertyBadge";
import { IpawsChannelBadge } from "shared/components/ipaws/IpawsChannelBadge";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { usePermissionCheck } from "shared/stores/oidc";
import { arr } from "shared/utils/array";
import { fail } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { IpawsAlertThumbnailFragment, IpawsCertificateType, IpawsChannel, Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { match } from "ts-pattern";

type IpawsTemplateCardProps = {
    ipawsTemplate: {
        id: string;
        alert: IpawsAlertThumbnailFragment;
    };
};

export const IpawsTemplateCard = ({ ipawsTemplate }: IpawsTemplateCardProps) => {
    const nav = useNavigate();

    const hasAlertCreate = usePermissionCheck(Permission.IpawsAlertModify, ipawsTemplate.alert.certificate.id);
    const hasAlertUpdate = usePermissionCheck(Permission.IpawsAlertModify, ipawsTemplate.alert.certificate.id);
    const hasAlertArchive = usePermissionCheck(Permission.CustomerIpawsManage);
    const hasCustomerManage = usePermissionCheck(Permission.CustomerIpawsManage);

    const { mutateAsync: duplicateTemplate } = useGraphqlMutation(mutations.duplicate);
    const { mutateAsync: archiveTemplate } = useGraphqlMutation(mutations.archive);

    function handleDuplicate(id: string) {
        void duplicateTemplate({ id })
            .then(() => queryClient.invalidateQueries({ queryKey: ["ipaws", "templates"] }))
            .then(() => notify.show.success("Duplicated IPAWS Alert Template"))
            .catch(notify.catch);
    }

    function handleArchive(id: string) {
        modals.openConfirmModal({
            title: "Archive IPAWS Alert Template",
            children: <Text size="sm">Are you sure you want to archive this IPAWS alert template?</Text>,
            labels: {
                confirm: "Archive",
                cancel: "Cancel",
            },
            onConfirm: () =>
                void archiveTemplate({ id })
                    .then(() => queryClient.invalidateQueries({ queryKey: ["ipaws", "templates"] }))
                    .then(() => notify.show.success("Archived IPAWS Alert Template"))
                    .catch(notify.catch),
        });
    }

    const enInfo = ipawsTemplate.alert.capMessage.infos.find((it) => it.language == "en-US") ?? fail("English info is required.");
    const evtCode =
        ipawsTemplate.alert.certificate.channels.flatMap((ch) => ch.eventCodes).find((ec) => ec.id == enInfo.eventCodes.at(0)) ??
        fail("Unknown or missing event code.");
    const channels = arr(Object.values(IpawsChannel))
        .except(...enInfo.blockedChannels)
        .take();

    const certificateName = hasCustomerManage ? (
        <Link to="/ipaws/certificates/$certId" params={{ certId: ipawsTemplate.alert.certificate.id }}>
            <Anchor>{ipawsTemplate.alert.certificate.name}</Anchor>
        </Link>
    ) : (
        ipawsTemplate.alert.certificate.name
    );

    return (
        <DetailCard
            key={ipawsTemplate.id}
            title={`${evtCode.id}: ${evtCode.description}`}
            subtitle={
                <Stack gap="xs">
                    <PropertyBadge title="Profile">{certificateName}</PropertyBadge>
                    <Group gap="xs">
                        Channels:
                        {channels.map((ch) => (
                            <IpawsChannelBadge key={ipawsTemplate.id + ch} type={ch} />
                        ))}
                    </Group>
                </Stack>
            }
            badge={match(ipawsTemplate.alert.certificate.type)
                .with(IpawsCertificateType.Live, () => (
                    <Badge color="red" my="sm">
                        Live
                    </Badge>
                ))
                .with(IpawsCertificateType.Test, () => (
                    <Badge color="blue" my="sm">
                        Test
                    </Badge>
                ))
                .exhaustive()}
            rightSection={
                <Link to="/ipaws/templates/send/$id" params={{ id: ipawsTemplate.id }}>
                    <Button size="compact-sm" variant="default" leftSection={<IconMailFast size={16} />} component="span">
                        Send
                    </Button>
                </Link>
            }
            actions={[
                hasAlertUpdate
                    ? {
                          icon: <IconEdit size={16} />,
                          label: "Edit",
                          onClick: () => nav({ to: "/ipaws/templates/$id", params: { id: ipawsTemplate.id } }),
                      }
                    : null,
                hasAlertCreate
                    ? { icon: <IconCopy size={16} />, label: "Duplicate", onClick: () => handleDuplicate(ipawsTemplate.id) }
                    : null,
                hasAlertArchive
                    ? { icon: <IconTrash size={16} />, label: "Archive", onClick: () => handleArchive(ipawsTemplate.id), color: "red" }
                    : null,
            ].filter((it) => !!it)}
        >
            <Spoiler
                maxHeight={80}
                styles={{ control: { width: "100%" } }}
                showLabel={
                    <>
                        <Group w="100%" justify="center" align="center" gap="2px">
                            <Button size="compact-xs" variant="transparent" component="a" rightSection={<IconChevronDown size={16} />}>
                                More
                            </Button>
                        </Group>
                    </>
                }
                hideLabel={
                    <Group w="100%" justify="center" align="center" gap="2px">
                        <Button size="compact-xs" variant="transparent" component="a" rightSection={<IconChevronUp size={16} />}>
                            Less
                        </Button>
                    </Group>
                }
            >
                <Typography>
                    {enInfo.headline}: {enInfo.description}
                </Typography>
            </Spoiler>
        </DetailCard>
    );
};

const mutations = {
    duplicate: graphql(`
        mutation IpawsTemplateListDuplicateTemplate($id: UUID!) {
            duplicateIpawsTemplate(id: $id) {
                id
            }
        }
    `),
    archive: graphql(`
        mutation IpawsTemplateListArchiveTemplate($id: UUID!) {
            archiveIpawsTemplate(id: $id) {
                id
            }
        }
    `),
};
